"use client";
import { useLayoutEffect, useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useTranslations } from "next-intl";

interface TimelineData {
  phase?: string;
  timeline?: string;
  content: string;
}

export default function Page() {
  const t = useTranslations('roadmap');
  const phases: TimelineData[] = [
    {
      phase: t('phase1'),
      timeline: "Q1-Q2 • 2022",
      content: t('phase1Description'),
    },
    {
      timeline: "Q3-Q4 • 2022",
      content: t('phase2Description'),
    },
    {
      timeline: "Q1-Q2 • 2023",
      content: t('phase3Description'),
    },
    {
      timeline: "Q3-Q4 • 2023",
      content: t('phase4Description'),
    },
    {
      timeline: "Q1 • 2024",
      content: t('phase5Description'),
    },
    {
      timeline: "Q2 • 2024",
      content: t('phase6Description'),
    },
    {
      phase: t('phase2'),
      timeline: "Q3-Q4 • 2024",
      content: t('phase7Description', { device: 2}),
    },
    {
      timeline: "Q1-Q2 • 2025",
      content: t('phase8Description'),
    },
    {
      timeline: "Q3-Q4 • 2025",
      content: t('phase9Description', { device: 3}),
    },
    {
      phase: t('phase3'),
      timeline: "Q1-Q2 • 2026",
      content: t('phase10Description'),
    },
    {
      timeline: "Q3-Q4 • 2026",
      content: t('phase11Description', { device: 8}),
    },
    {
      timeline: "Q1-Q4 • 2027",
      content: t('phase12Description', { device: 15}),
    },
    {
      timeline: "Q1-Q4 • 2028",
      content: t('phase13Description', { device: 30}),
    },
    {
      timeline: "Q1-Q4 • 2029",
      content: t('phase14Description', { device: 50}),
    },
    {
      timeline: "Q1-Q4 • 2030",
      content: t('phase15Description', { device: 100}),
    },
  ];

  const flattenedData = phases.flatMap((item) => {
    const result: { type: "timeline" | "phase" | "content"; value: string }[] = [];

    if (item.phase) {
      result.push({ type: "phase", value: item.phase });
    }

    if (item.timeline) {
      result.push({ type: "timeline", value: item.timeline });
    }

    if (item.content) {
      item.content.split(",").forEach((c) => {
        result.push({ type: "content", value: c.trim() });
      });
    }

    return result;
  });


  const pinContainerRef = useRef<HTMLDivElement>(null);
  const horizontalTrackRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const pinContainer = pinContainerRef.current;
    const track = horizontalTrackRef.current;

    if (!pinContainer || !track) return;

    // Simple parallax effect for background
    gsap.to(track, {
      yPercent: -10,
      ease: "none",
      scrollTrigger: {
        trigger: pinContainer,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <div className="p-20 max-lg:p-10 max-md:p-6 max-sm:p-4 text-center">
        <h1 className="text-[56px]/[64px] max-lg:text-[48px]/[56px] max-md:text-[40px]/[48px] max-sm:text-[28px]/[36px] tracking-[-1px] font-bold">Hyra Network</h1>
        <p className="text-[56px]/[64px] max-lg:text-[48px]/[56px] max-md:text-[40px]/[48px] max-sm:text-[28px]/[36px] tracking-[-1px] font-bold">
          {t('title')}
        </p>
        <p className="mt-5 text-lg max-lg:text-base max-md:text-sm max-sm:text-xs font-light">
          {t('subTitle')}
        </p>
      </div>
      <div className="relative min-h-[800vh]">
        {/* Background container */}
        {/* <div
          ref={pinContainerRef}
          className="fixed inset-0 z-0"
        >
          <div
            ref={horizontalTrackRef}
            className="absolute left-0 top-0 h-full w-full opacity-30"
          >
            <Image
              src="/images/backgrounds/map.png"
              alt="bg"
              fill
              priority
              sizes="100vw"
              style={{
                objectFit: "cover",
                objectPosition: "center top",
              }}
            />
          </div>
        </div> */}

        <Image
          src="/images/backgrounds/map.png"
          alt="bg"
          fill
          priority
          sizes="100vw"
          className="absolute inset-0 z-0 opacity-30 max-h-screen object-cover object-center"
        />

        {/* Main content */}
        <div className="relative z-10">
          <div className="absolute inset-0"></div>
          <div className="relative mx-auto max-w-[1440px] px-[78px] max-lg:px-10 max-md:px-6 max-sm:px-4 py-20 max-lg:py-16 max-md:py-12 max-sm:py-8">
            <div className="relative min-h-[800vh]">
              {/* Vertical line */}
              <div className="absolute left-1/3 max-lg:left-1/2 max-sm:left-0 h-full w-0.5 -translate-x-px transform bg-[#599EFF]"></div>

              {flattenedData.map((item, index) => (
                <div key={index} className="relative">
                  {/* Timeline dot */}
                  {/* <div
                        className={`absolute left-1/3 z-10 h-3 w-3 -translate-x-1/2 transform rounded-full border-4 border-white bg-blue-500 shadow-md ${item.type === "phase" ? "top-0" : "top-1/2 -translate-y-1/2"} `}
                    /> */}
                  <div
                    className={`absolute left-1/3 max-lg:left-1/2 max-sm:left-0 z-0 h-3 w-3 -translate-x-1/2 transform ${item.type === "phase" ? "top-0" : "top-1/2 -translate-y-1/2"} `}
                  >
                    {item.type === "content" && (
                      <Image
                        src={"/images/icons/square-45.svg"}
                        width={30}
                        height={30}
                        alt="logo"
                      />
                    )}
                    {(item.type === "phase" || item.type === "timeline") && (
                      <Image
                        src={"/images/icons/logo-network.svg"}
                        width={30}
                        height={30}
                        alt="logo"
                        className="w-20"
                      />
                    )}
                  </div>

                  <div className="flex min-h-[70px] items-start pt-4">
                    {/* Left side - Timeline */}
                    <div className="w-1/3 pr-8 max-lg:w-1/2 max-lg:pr-8 max-md:w-full text-right max-sm:hidden">
                      {item.type === "timeline" && (
                        <div className="text-4xl max-lg:text-[32px]/[40px] max-md:text-[24px]/[32px] text-[#202020]">
                          {item.value}
                        </div>
                      )}
                    </div>

                    {/* Right side - Phase and Content */}
                    <div className="w-2/3 max-lg:w-1/2 max-md:w-full pl-8 max-md:pl-4 max-sm:pl-8 max-sm:w-full">
                      {item.type === "timeline" && (
                        <div className="mt-14 text-5xl max-lg:text-[32px]/[40px] text-[#202020] sm:hidden">
                          {item.value}
                        </div>
                      )}

                      {item.type === "phase" && (
                        <div className="mt-14 text-5xl max-lg:text-[32px]/[40px] text-[#202020]">
                          {item.value}
                        </div>
                      )}

                      {item.type === "content" && (
                        <div className="text-2xl max-lg:text-xl font-light tracking-[-1px]">
                          {item.value}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
